import React from "react";
import { formatDuration } from "../../../utils/dateHelpers";
import {TABLE_DATE_FORMAT} from "../../../utils/Constants";
import moment from "moment";
import {tokens} from "../../../public/tokens";
import styled from "styled-components";
const dateToday = moment().format("YYYY-MM-DD");
const BackgroundColorSpan = styled.span`
    background-color: ${({$color}) => $color};
    color: #fff;
    text-transform: none;
    margin-right: 0.5rem;
    padding: 3px 5px;
    border-radius: 2px;
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 400;
    `;

/**
 * Create an empty milestone object with default values.
 * @returns {{id: null, template_milestone_id: null, name: string, sequence: number, expected_date: string, cardinal_date: string, status: string}}
 */
export const emptyMilestone = () => ({
  id: null,
  template_milestone_id: null,
  name: "",
  sequence: 0,
  expected_date: "",
  cardinal_date: "",
  status: "Not Started",
});

/**
 * Create an empty resource object with default values.
 * @returns {{id: null, template_resource_id: null, name: string, url: string, is_required: boolean}}
 */
export const emptyResource = () => ({
  id: null,
  template_resource_id: null,
  name: "",
  url: "",
  is_required: false,
});

/**
 * Determine the color for a milestone date based on completion and lateness.
 * If no date is provided, or the milestone is overdue and incomplete, returns an alert color.
 * @param {string} date - The milestone date string.
 * @param {string} milestone - The milestone status.
 * @returns {string|undefined} A color token string or undefined if the milestone is complete and not overdue.
 */
export const dateColorByMilestone = (date, milestone) => {
  if (!date) return tokens.colors.states.alert[500];
  if (moment(date).unix() < moment(dateToday).unix() && milestone !== "Complete") {
    return tokens.colors.states.alert[500];
  }
};

/**
 * Validate whether a string is a well-formed URL.
 * @param {string} url - The URL string to validate.
 * @returns {boolean} True if the URL is valid; otherwise false.
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
/**
 * Derives project-milestone summary metrics for display in the project list.
 *
 * Calculates completion percentage, the next incomplete milestone and its date,
 * and duration/time-left/end-date values using expected dates first and cardinal
 * dates as a fallback.
 *
 * @param {Array<Object>} milestones - Array of milestone objects containing status, expected_date,
 * cardinal_date, sequence, and name values.
 * @param {string|Date} [startDate] - Project start date used to calculate duration.
 * @returns {{completion: string, nextDeliverableDate: string|null, nextDeliverable: string, DurationExpected: string, DurationCardinal: string, TimeLeftExpected: string, TimeLeftCardinal: string, EndDateExpected: string, EndDateCardinal: string}}
 */
export const processMilestoneData = (milestones, startDate) => {
    if (!milestones?.length) {
        return {
            completion: "0%",
            nextDeliverableDate: null,
            nextDeliverable: "-",
            estimateDuration: "-",
            estimateTimeLeft: "-",
            estimateEndDate: "-",
        };
    }

    const completedMilestones = milestones.filter(
        (milestone) => milestone.status === "Complete"
    );
    const incompleteMilestones = milestones.filter(
        (milestone) => milestone.status !== "Complete"
    );

    // Next deliverable:    
    const nextMilestone =
        [...incompleteMilestones]
            .filter((milestone) => milestone.expected_date && milestone.status !== "Complete")
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone1.expected_date).unix() - moment(milestone2.expected_date).unix()
            )[0] ||
        [...incompleteMilestones]
            .filter((milestone) => milestone.cardinal_date && milestone.status !== "Complete")
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone1.cardinal_date).unix() - moment(milestone2.cardinal_date).unix()
            )[0] ||
        [...incompleteMilestones].sort((milestone1, milestone2) => milestone1.sequence - milestone2.sequence)[0];

    const nextDeliverable = nextMilestone?.name || "-";
    const nextDeliverableDate = nextMilestone?.expected_date || nextMilestone?.cardinal_date || null;

    // Estimate end date: latest expected_date across all milestones; only fall
    // back to the latest cardinal_date if no milestone has an expected_date at all.
    const estimateEndMilestone =
        [...milestones]
            .filter((milestone) => milestone.expected_date)
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone2.expected_date).unix() - moment(milestone1.expected_date).unix()
            )[0]
    const estimateEndCardinalMilestone = [...milestones]
            .filter((milestone) => milestone.cardinal_date)
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone2.cardinal_date).unix() - moment(milestone1.cardinal_date).unix()
            )[0];
    const EndDateExpected = estimateEndMilestone?.expected_date || null;
    const EndDateCardinal = estimateEndCardinalMilestone?.cardinal_date || null;

    const DurationExpected = (startDate && EndDateExpected)
        ? formatDuration(startDate.split("T")[0], EndDateExpected)
        : "-";

    const TimeLeftExpected = EndDateExpected
        ? formatDuration(moment().format('YYYY-MM-DD'), EndDateExpected)
        : "-";
    const Durationcardinal = (startDate && EndDateCardinal)
        ? formatDuration(startDate.split("T")[0], EndDateCardinal)
        : "-";
    const cardinalTimeLeftcardinal = EndDateCardinal
        ? formatDuration(moment().format('YYYY-MM-DD'), EndDateCardinal)
        : "-";

    return {
        completion: `${Math.round((completedMilestones.length / milestones.length) * 100)}%`,
        nextDeliverableDate: nextDeliverableDate,
        nextDeliverable: nextDeliverable,
        DurationExpected: DurationExpected,
        DurationCardinal: Durationcardinal,
        TimeLeftExpected: TimeLeftExpected,
        TimeLeftCardinal: cardinalTimeLeftcardinal,
        EndDateExpected: EndDateExpected || "-",
        EndDateCardinal: EndDateCardinal || "-",
    };
}

/**
 * Format a date value using the configured table date format.
 * @param {string|Date|null|undefined} value - The date value to format.
 * @returns {string} Formatted date string or "-" when the value is invalid.
 */
export const formatDate = (value) => {
  if (!value || !moment(value).isValid()) return "-";
  return moment(value).format(TABLE_DATE_FORMAT); // or "DD/MM/YYYY"
};

/**
 * Render department display metadata for a project center row.
 * @param {string|number} data - The department identifier.
 * @param {Array} departmentFilterData - The department filter data.
 * @returns {{color: string, text: string, html: JSX.Element}|string} Department display metadata or "-".
 */
export const RenderDepartment = (data, departmentFilterData) => {
    const department = departmentFilterData.find((filter) => Number(filter.value) === Number(data));
    console.log("data", data);
    console.log("departmentFilterData", departmentFilterData);
    console.log("department", department);
    if (department) {
        return {
            color: department?.brandColor ? tokens.colors.brand[department.brandColor][800] : tokens.colors.neutral.grey[800],
            text: department.label || "-",
            html: (
                <BackgroundColorSpan
                    title={department.label || "-"}
                    $color={department?.brandColor ? tokens.colors.brand[department.brandColor][800] : tokens.colors.neutral.grey[800]}
                >
                    {department.label || "-"}
                </BackgroundColorSpan>
            ),
        }
    }
    return "-";
}